import { Log } from '../core/logger'
import { closeDialog, closeOnBackdropClick, openDialog } from '../core/dialog-transition'
import { reportFrontendError } from '../core/errors'
import { parseToastDetails, type ToastDetails } from '../core/toast-details'
import { showActionNotification } from '../core/notifications'

const initializedSettings = new WeakSet<Element>()

/** Verifies provider credentials and reveals models only after an explicit check. */
export function initializeAiImportSettings(root: ParentNode = document): void {
  root.querySelectorAll('[data-ai-import-settings]').forEach((settings) => {
    if (initializedSettings.has(settings)) return

    const apiKey = settings.querySelector<HTMLInputElement>('[data-ai-api-key]')
    const apiKeyRow = settings.querySelector<HTMLElement>('[data-ai-key-row]')
    const apiKeyHelp = settings.querySelector<HTMLElement>('[data-ai-key-help]')
    const controls = settings.querySelector<HTMLElement>('[data-ai-model-controls]')
    const provider = settings.querySelector<HTMLSelectElement>('[data-ai-provider]')
    const model = settings.querySelector<HTMLSelectElement>('[data-ai-model]')
    const modelValue = settings.querySelector<HTMLInputElement>('[data-ai-model-value]')
    const modelStatus = settings.querySelector<HTMLElement>('[data-ai-model-status]')
    const saveHelp = settings.querySelector<HTMLElement>('[data-ai-save-help]')
    const saveButton = settings.querySelector<HTMLButtonElement>('[data-ai-settings-save]')
    const validateButton = settings.querySelector<HTMLButtonElement>('[data-ai-key-validate]')
    const endpointRow = settings.querySelector<HTMLElement>('[data-ai-endpoint-row]')
    const endpoint = settings.querySelector<HTMLInputElement>('[data-ai-endpoint]')
    const guideButton = settings.querySelector<HTMLButtonElement>('[data-ai-guide-open]')
    const guideId = guideButton?.getAttribute('aria-controls') ?? ''
    const guideDialog = guideId === ''
      ? null
      : settings.ownerDocument.getElementById(guideId) as HTMLDialogElement | null
    const guidePanels = guideDialog?.querySelectorAll<HTMLElement>('[data-ai-guide-provider]') ?? []

    if (!apiKey || !controls) return

    initializedSettings.add(settings)

    const keyWasConfigured = settings.getAttribute('data-api-key-configured') === 'true'
    const configuredProvider = settings.getAttribute('data-configured-provider')
    const configuredEndpoint = settings.getAttribute('data-configured-endpoint') ?? ''
    const configuredModel = settings.getAttribute('data-configured-model') ?? ''
    let credentialValidated = false
    let modelRequestRunning = false

    /** Rebuilds the model picker without accepting free-form model identifiers. */
    const replaceModels = (models: string[], selectedModel = ''): void => {
      if (!model) return

      const placeholder = document.createElement('option')
      placeholder.value = ''
      placeholder.textContent = settings.getAttribute('data-ai-model-select') ?? ''
      placeholder.disabled = true
      placeholder.selected = selectedModel === '' || !models.includes(selectedModel)

      const options = models.map((modelId) => {
        const option = document.createElement('option')
        option.value = modelId
        option.textContent = modelId
        option.selected = modelId === selectedModel

        return option
      })

      model.replaceChildren(placeholder, ...options)

      if (modelValue) {
        modelValue.value = selectedModel
      }
    }

    /** Loads models through the authenticated administrator endpoint. */
    const loadModels = async (): Promise<void> => {
      if (modelRequestRunning) return

      modelRequestRunning = true

      try {
        await Log.info('Load available AI provider models', async () => {
          const form = settings.closest<HTMLFormElement>('form')
          const csrf = form?.querySelector<HTMLInputElement>('input[name="_csrf"]')
          const url = settings.getAttribute('data-ai-models-url') ?? ''

          if (!provider || !csrf || url === '') {
            throw reportFrontendError(new Error('AI model picker configuration is incomplete.'))
          }

          const payload = new FormData()
          payload.set('_csrf', csrf.value)
          payload.set('provider', provider.value)
          payload.set('api_key', apiKey.value)
          payload.set('endpoint', endpoint?.value ?? '')

          if (model) model.disabled = true
          if (saveButton) saveButton.disabled = true
          if (validateButton) validateButton.disabled = true
          if (modelStatus) {
            modelStatus.textContent = settings.getAttribute('data-ai-model-loading') ?? ''
          }

          let userFailureMessage = settings.getAttribute('data-ai-model-failed') ?? ''
          let toastDetails: ToastDetails | undefined

          try {
            /** Sends one idempotent model-catalog request with the current form state. */
            const requestModels = (): Promise<Response> => fetch(url, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-CSRF-Token': csrf.value,
              },
              body: payload,
            })

            const response = await requestModels()

            const result: unknown = await response.json()

            if (typeof result !== 'object' || result === null) {
              throw new TypeError('AI model catalog response must be an object.')
            }

            const record = result as Record<string, unknown>
            const models = Array.isArray(record.models)
              ? record.models.filter((item): item is string => typeof item === 'string')
              : []

            if (!response.ok || models.length === 0) {
              userFailureMessage = typeof record.error === 'string'
                ? record.error
                : models.length === 0
                  ? settings.getAttribute('data-ai-model-empty') ?? ''
                  : userFailureMessage

              try {
                toastDetails = parseToastDetails(record.details)
              } catch (error) {
                const reason = error instanceof Error ? error.message : typeof error
                Log.warning(`Ignore invalid toast details: ${reason}`)
                toastDetails = undefined
              }

              throw new Error(`AI model catalog failed with HTTP status ${response.status}.`)
            }

            const previous = model?.value ?? ''
            replaceModels(models, previous)
            credentialValidated = true

            const loadedMessage = settings
              .getAttribute('data-ai-model-loaded')
              ?.replace('{count}', String(models.length)) ?? ''

            if (modelStatus) modelStatus.textContent = ''
            if (loadedMessage !== '') showActionNotification(loadedMessage, 'success')
          } catch (error: unknown) {
            if (modelStatus) modelStatus.textContent = ''

            throw reportFrontendError(error, userFailureMessage, toastDetails)
          } finally {
            if (model) model.disabled = false
            if (saveButton) saveButton.disabled = false
            if (validateButton) validateButton.disabled = false
            updateControls()
          }
        })
      } finally {
        modelRequestRunning = false
      }
    }

    /** Shows guidance for the provider currently selected in the form. */
    const updateGuide = (): void => {
      guidePanels.forEach((panel) => {
        panel.hidden = panel.dataset.aiGuideProvider !== provider?.value
      })
    }

    /** Synchronizes dependent controls with the currently available credential. */
    const updateControls = (): void => {
      Log.notice('Update AI import controls after API key input', () => {
        const storedKeyMatchesProvider = keyWasConfigured
          && provider?.value === configuredProvider
          && (endpoint?.value.trim() ?? '') === configuredEndpoint
          && apiKey.value.trim() === ''
        const providerIsNone = provider?.value === 'none'
        apiKey.placeholder = storedKeyMatchesProvider
          ? apiKey.dataset.keptPlaceholder ?? ''
          : ''

        if (apiKeyRow) apiKeyRow.hidden = providerIsNone
        if (apiKeyHelp) apiKeyHelp.hidden = providerIsNone

        controls.hidden = providerIsNone
        if (model) {
          model.disabled = !credentialValidated
          model.required = credentialValidated
        }

        const modelHelp = settings.querySelector<HTMLElement>('[data-ai-model-help]')

        if (modelHelp) {
          modelHelp.hidden = providerIsNone || credentialValidated
        }

        const customProvider = provider?.value === 'compatible'
        const endpointMissing = customProvider && endpoint?.value.trim() === ''

        if (endpointRow) endpointRow.hidden = !customProvider
        if (endpoint) endpoint.required = customProvider
        if (saveHelp) {
          saveHelp.hidden = storedKeyMatchesProvider || providerIsNone || credentialValidated
        }
        if (validateButton) {
          validateButton.disabled = providerIsNone
            || (apiKey.value.trim() === '' && !storedKeyMatchesProvider)
            || endpointMissing
        }
        if (saveButton) {
          const aiConfigurationChanged = provider?.value !== configuredProvider
            || apiKey.value.trim() !== ''
            || (endpoint?.value.trim() ?? '') !== configuredEndpoint
            || (modelValue?.value ?? '') !== configuredModel

          saveButton.disabled = !providerIsNone
            && aiConfigurationChanged
            && !credentialValidated
        }

        updateGuide()
      })
    }

    /** Prevents a model from one service being submitted for another service. */
    const updateProvider = (): void => {
      apiKey.value = ''
      credentialValidated = false

      if (model) {
        const selectedModel = provider?.value === configuredProvider
          ? configuredModel
          : ''

        replaceModels(selectedModel === '' ? [] : [selectedModel], selectedModel)
      }

      updateControls()
    }

    /** Invalidates a prior check whenever credential-dependent input changes. */
    const updateCredential = (): void => {
      credentialValidated = false
      updateControls()
    }

    /** Loads the model catalog after the credential or endpoint is finished. */
    const loadModelsWhenReady = (): void => {
      const customProviderNeedsEndpoint = provider?.value === 'compatible'
        && endpoint?.value.trim() === ''
      const storedKeyMatchesProvider = keyWasConfigured
        && provider?.value === configuredProvider
        && (endpoint?.value.trim() ?? '') === configuredEndpoint
        && apiKey.value.trim() === ''
      const keyIsAvailable = apiKey.value.trim() !== '' || storedKeyMatchesProvider

      if (provider?.value === 'none' || !keyIsAvailable || customProviderNeedsEndpoint) return

      void loadModels()
    }

    apiKey.addEventListener('input', updateCredential)
    apiKey.addEventListener('change', updateControls)
    apiKey.addEventListener('focus', updateControls)
    provider?.addEventListener('change', updateProvider)
    endpoint?.addEventListener('input', updateCredential)
    endpoint?.addEventListener('change', updateControls)
    model?.addEventListener('change', () => {
      if (modelValue) modelValue.value = model.value
      updateControls()
    })
    validateButton?.addEventListener('click', loadModelsWhenReady)
    guideButton?.addEventListener('click', () => {
      if (guideDialog) openDialog(guideDialog)
    })
    guideDialog?.querySelectorAll<HTMLButtonElement>('[data-ai-guide-close]').forEach((button) => {
      button.addEventListener('click', () => {
        void closeDialog(guideDialog)
      })
    })
    if (guideDialog) {
      closeOnBackdropClick(guideDialog)
    }
    guideDialog?.addEventListener('cancel', (event) => {
      event.preventDefault()
      void closeDialog(guideDialog)
    })
    updateControls()
  })
}
